import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import {
  NotificationActions,
  NotificationService,
  NotificationServiceInjector,
} from '@next/core-lib/notification';
import { MergedRoute } from '@next/core-lib/router-store';
import { SessionActions } from '@next/core-lib/session';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { Action, Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { PigContextKeys } from '../../../shared/models/shared.model';
import { SettingsServiceHelper } from '../../../shared/services/settingsHelperService';
import {
  Barn,
  Discount,
  DosatorSetting,
  EnergySystem,
  LysineSystem,
  SettingModuleState,
  Silo,
  UpsertBarnResponse,
  UpsertDosatorSettingResponse,
} from '../../models/settings.model';
import {
  LeeOAuthentication,
  LeeOAuthenticationUpdatedNotification as LeeOAuthenticationUpsertedNotification,
  LeeOAuthenticationUpsertedResponse,
  NedapInstallation,
  UpdateNedapInstallationMetaDataResponse,
} from '../../models/third-parties.model';
import { BarnService } from '../../services/barn.service';
import { DiscountService } from '../../services/discount.service';
import { DosatorSettingService } from '../../services/dosator-setting.service';
import { LeeOIntegrationService } from '../../services/lee-o-integration.service';
import { NedapIntegrationService } from '../../services/nedap-integration.service';
import { SiloService } from '../../services/silo.service';
import * as settingActions from '../actions/settings.actions';
import { loadEnergySetting, loadLysineSetting, setUomSetting } from '../actions/settings.actions';
import {
  getSelectedBarn,
  getSelectedBarnId,
  getSelectedDosatorSetting,
  getSelectedDosatorSettingId,
} from '../selectors/settings.selectors';

@Injectable()
export class SettingEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store<SettingModuleState>,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly settingsHelper: SettingsServiceHelper,
    private readonly barnService: BarnService,
    private readonly siloService: SiloService,
    private readonly discountService: DiscountService,
    private readonly dialogService: DialogService,
    @Inject(LocaleService) private locale: LocaleService,
    private readonly nedapIntegrationService: NedapIntegrationService,
    private readonly leeOIntegrationService: LeeOIntegrationService,
    private readonly dosatorSettingService: DosatorSettingService,
    @Inject(NotificationServiceInjector) private readonly notification: NotificationService,
  ) {}

  loadEnergySetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEnergySetting),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            EnergySystem.EW,
            PigContextKeys.UOM_ENERGY_SYSTEM,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            map((value: string) =>
              setUomSetting({ system: PigContextKeys.UOM_ENERGY_SYSTEM, value, save: false }),
            ),
          ),
      ),
      catchError((_: HttpErrorResponse) => EMPTY),
    ),
  );

  loadLysineSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLysineSetting),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            LysineSystem.SID,
            PigContextKeys.UOM_LYSINE_SYSTEM,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            map((value: string) =>
              setUomSetting({ system: PigContextKeys.UOM_LYSINE_SYSTEM, value, save: false }),
            ),
          ),
      ),
      catchError((_: HttpErrorResponse) => EMPTY),
    ),
  );

  setUomSetting$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setUomSetting),
        tap((action) => {
          if (action.save === true) {
            this.settingsHelper.setSettings(
              action.system,
              action.value,
              SettingsStorage.Global,
              true,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadSilos$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadSilosRequest),
      switchMap((_) =>
        this.store.pipe(
          select(UserSelectors.selectLocation),
          skipWhile((location) => location === null || location === undefined),
          take(1),
        ),
      ),
      mergeMap(() =>
        this.siloService.getSilos().pipe(
          switchMap((silos: Silo[]) => of(settingActions.loadSilosSuccess({ silos }))),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadSilosFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadBarns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadBarnsRequest),
      mergeMap(() =>
        this.barnService.getBarns().pipe(
          withLatestFrom(this.store.select(getSelectedBarnId)),
          switchMap(([barns, barnId]: [Barn[], number]) =>
            of(
              settingActions.loadBarnsSuccess({ barns }),
              settingActions.setSelectedBarn({ barnId }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadBarnsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteBarnRequest),
      mergeMap(({ barn }) =>
        this.barnService.deleteBarn(barn.id).pipe(
          map(() => settingActions.deleteBarnSuccess({ barn })),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.deleteBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveSelectedBarn$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertBarn),
      switchMap((_) => this.store.pipe(select(getSelectedBarn), take(1))),
      map((barn: Barn) => settingActions.upsertBarnRequest({ barn })),
    ),
  );

  upsertBarnRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertBarnRequest),
      exhaustMap(({ barn }) =>
        this.barnService.upsertBarn(barn).pipe(
          map((response: UpsertBarnResponse) =>
            settingActions.upsertBarnSuccess({
              barn: {
                ...barn,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertBarnFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  changeNavigationUrl$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map(
        (action: RouterNavigationAction<MergedRoute>) =>
          action.payload.routerState.params['barnId'],
      ),
      map((barnId) => settingActions.setSelectedBarn({ barnId: parseInt(barnId, 10) })),
    ),
  );

  upsertBarnRequestSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.upsertBarnSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/settings', 'barn'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteBarnWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteBarnWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('barn-item')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                settingActions.deleteBarnRequest({
                  barn: action.barn,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  loadDiscount$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadDiscountRequest),
      mergeMap(() =>
        this.discountService.getDiscount().pipe(
          switchMap((discount: Discount) => of(settingActions.loadDiscountSuccess({ discount }))),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadDiscountFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertDiscountRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertDiscountRequest),
      exhaustMap(({ discount }) =>
        this.discountService.upsertDiscount(discount).pipe(
          map((response: UpsertBarnResponse) =>
            settingActions.upsertDiscountSuccess({ id: response.id }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertDiscountFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadNedapInstallationsForActiveSession$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.setUserSession),
      debounceTime(100),
      map((_) => settingActions.loadNedapInstallationsRequest()),
    ),
  );

  loadNedapInstallations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        settingActions.loadNedapInstallationsRequest,
        settingActions.updateNedapInstallationMetaDataSuccess,
      ),
      mergeMap((_) =>
        this.nedapIntegrationService.getInstallations().pipe(
          map((nedapInstallations: NedapInstallation[]) =>
            settingActions.loadNedapInstallationsSuccess({
              nedapInstallations,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadNedapInstallationsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertNedapInstallationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.updateNedapInstallationMetaDataRequest),
      exhaustMap(({ nedapInstallation }) =>
        this.nedapIntegrationService.updateNedapInstallationMetaData(nedapInstallation).pipe(
          map((response: UpdateNedapInstallationMetaDataResponse) =>
            settingActions.updateNedapInstallationMetaDataSuccess({
              nedapInstallation: response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.updateNedapInstallationMetaDataFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadLeeOAuthentication$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadLeeOAuthenticationRequest),
      mergeMap(() =>
        this.leeOIntegrationService.getAuthentication().pipe(
          map((leeOAuthentication: LeeOAuthentication) =>
            settingActions.loadLeeOAuthenticationSuccess({
              leeOAuthentication,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadLeeOAuthenticationFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    ),
  );

  upsertLeeOAuthenticationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertLeeOAuthenticationRequest),
      exhaustMap(({ upsertLeeOAuthenticationRequest }) =>
        this.leeOIntegrationService.upsertAuthentication(upsertLeeOAuthenticationRequest).pipe(
          map((response: LeeOAuthenticationUpsertedResponse) =>
            settingActions.upsertLeeOAuthenticationSuccess({
              leeOAuthenticationUpsertedResponse: response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertLeeOAuthenticationFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  leeOAuthenticationUpdatedNotification$ = createEffect(() =>
    this.notification.on<LeeOAuthenticationUpsertedNotification>('AuthenticationUpserted').pipe(
      map(() => settingActions.loadLeeOAuthenticationRequest()),
      catchError(() => EMPTY),
    ),
  );

  leeOAuthenticationDeletedNotification$ = createEffect(() =>
    this.notification.on('AuthenticationDeleted').pipe(
      map(() => settingActions.loadLeeOAuthenticationRequest()),
      catchError(() => EMPTY),
    ),
  );

  deleteLeeOAuthenticationWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteLeeOAuthenticationWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: 'delete title',
            text: 'delete text',
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  settingActions.deleteLeeOAuthenticationRequest({
                    leeOAuthenticationId: action.leeOAuthenticationId,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteLeeOAuthenticationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteLeeOAuthenticationRequest),
      mergeMap((action) =>
        this.leeOIntegrationService.deleteAuthentication(action.leeOAuthenticationId).pipe(
          map(() =>
            settingActions.deleteLeeOAuthenticationSuccess({
              leeOAuthenticationId: action.leeOAuthenticationId,
            }),
          ),
          catchError((_: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({
                message: this.locale.translate('Deleted'),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadDosatorSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.loadDosatorSettingsRequest),
      mergeMap(() =>
        this.dosatorSettingService.getDosatorSettings().pipe(
          withLatestFrom(this.store.select(getSelectedDosatorSettingId)),
          switchMap(([dosatorSettings, dosatorSettingId]: [DosatorSetting[], number]) =>
            of(
              settingActions.loadDosatorSettingsSuccess({ dosatorSettings }),
              settingActions.setSelectedDosatorSetting({ dosatorSettingId }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.loadDosatorSettingsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteDosatorSettingRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteDosatorSettingRequest),
      mergeMap(({ dosatorSetting: dosatorSetting }) =>
        this.dosatorSettingService.deleteDosatorSetting(dosatorSetting.id).pipe(
          map(() => settingActions.deleteDosatorSettingSuccess({ dosatorSetting })),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.deleteDosatorSettingFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveSelectedDosatorSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertDosatorSetting),
      switchMap((_) => this.store.pipe(select(getSelectedDosatorSetting), take(1))),
      map((dosatorSetting: DosatorSetting) =>
        settingActions.upsertDosatorSettingRequest({ dosatorSetting }),
      ),
    ),
  );

  upsertDosatorSettingRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.upsertDosatorSettingRequest),
      exhaustMap(({ dosatorSetting: dosatorSetting }) =>
        this.dosatorSettingService.upsertDosatorSetting(dosatorSetting).pipe(
          map((response: UpsertDosatorSettingResponse) =>
            settingActions.upsertDosatorSettingSuccess({
              dosatorSetting: {
                ...dosatorSetting,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              settingActions.upsertDosatorSettingFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  changeDosatorSettingNavigationUrl$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map(
        (action: RouterNavigationAction<MergedRoute>) =>
          action.payload.routerState.params['dosatorsettingId'],
      ),
      map((dosatorSettingId) =>
        settingActions.setSelectedDosatorSetting({
          dosatorSettingId: parseInt(dosatorSettingId, 10),
        }),
      ),
    ),
  );

  upsertDosatorSettingRequestSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(settingActions.upsertDosatorSettingSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/settings', 'dosator-setting'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteDosatorSettingWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(settingActions.deleteDosatorSettingWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning(this.getDosatorSettingDeleteDialogConfimation('dosatorsetting-item'))
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  settingActions.deleteDosatorSettingRequest({
                    dosatorSetting: action.dosatorSetting,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  private getDeleteDialogConfimation(type: string) {
    return {
      text: this.locale.translate(`settings.barn.delete-${type}`),
    };
  }

  private getDosatorSettingDeleteDialogConfimation(type: string) {
    return {
      text: this.locale.translate(`settings.dosator-setting.delete-${type}`),
    };
  }
}
